import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Checkbox, Tag, Dropdown, Modal, message, Alert } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { connectorAttributeMappings, attributes } from "../../../apiService/blustreamApi";
import AttributeMappingModal from "./AttributeMappingModal";

const ATTRIBUTE_NAMES_PAGE_SIZE = 100;
// Same safety-valve reasoning as MAX_MAPPING_PAGES below, applied to the
// attribute-name lookup fetch.
const MAX_ATTRIBUTE_NAME_PAGES = 50;

// Resolves data_class_attribute_id -> display name for the "Polly Attribute"
// column and the delete-confirmation text. The mapping API deliberately
// omits attribute_name -- callers are expected to resolve it themselves from
// /api/beta/attributes -- so this is a second, independent fetch from
// fetchAllMappings below. Callers must treat failure here as non-fatal: an
// empty map just means every row falls back to `Attribute #<id>`.
async function fetchAttributeNameMap() {
  const map = {};
  for (let page = 0; page < MAX_ATTRIBUTE_NAME_PAGES; page += 1) {
    const res = await attributes.list({ page, size: ATTRIBUTE_NAMES_PAGE_SIZE });
    const content = Array.isArray(res?.content) ? res.content : [];
    content.forEach((a) => {
      if (a?.attribute_id != null) map[a.attribute_id] = a.name;
    });
    if (!res?.page || res.page.last || content.length === 0) break;
  }
  return map;
}

const MAPPINGS_PAGE_SIZE = 100;
// Safety valve only. The API is scoped per-tenant and this UI has no
// pagination controls of its own, so we page through the whole collection
// in-memory (fine at tens/hundreds of mappings per open question in the
// ch60570 plan). If a tenant somehow has more than this many mappings we
// stop fetching and surface a visible banner instead of looping forever or
// silently dropping rows.
const MAX_MAPPING_PAGES = 50;

// Fetches every page of the collection (server derives tenant scope), so
// the table never silently drops rows past a single page's worth. Returns
// whatever it managed to collect plus enough info to tell the caller
// whether that's everything the server has.
async function fetchAllMappings() {
  let all = [];
  let pageMeta = null;
  for (let page = 0; page < MAX_MAPPING_PAGES; page += 1) {
    const res = await connectorAttributeMappings.list({ page, size: MAPPINGS_PAGE_SIZE });
    const content = Array.isArray(res?.content) ? res.content : [];
    all = all.concat(content);
    pageMeta = res?.page || null;
    if (!pageMeta || pageMeta.last || content.length === 0) break;
  }
  const totalElements = typeof pageMeta?.total_elements === "number" ? pageMeta.total_elements : all.length;
  return { content: all, totalElements };
}

export default function AttributeMappingsTab({ subscriptionId }) {
  const [mappings, setMappings] = useState([]);
  const [attributeNames, setAttributeNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Tracked independently of `mappings` (which may be narrowed by the
  // "show enabled only" filter below) so the truncation banner's counts
  // always describe the true server-side total, not the filtered view.
  const [fetchedCount, setFetchedCount] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      // The API has no "enabled only" endpoint (that was a legacy path);
      // fetch the full collection and filter client-side instead.
      // Name resolution is fetched alongside but caught independently -- a
      // failure to load names must never take down the mappings table.
      const [{ content, totalElements }, nameMap] = await Promise.all([
        fetchAllMappings(),
        fetchAttributeNameMap().catch(() => ({})),
      ]);
      setFetchedCount(content.length);
      setHiddenCount(Math.max(totalElements - content.length, 0));
      setMappings(showEnabledOnly ? content.filter((m) => m.enabled) : content);
      setAttributeNames(nameMap);
    } catch (err) {
      message.error("Failed to load attribute mappings");
      setMappings([]);
      setFetchedCount(0);
      setHiddenCount(0);
    }
    setLoading(false);
  }, [showEnabledOnly]);

  useEffect(() => {
    if (subscriptionId) {
      fetchMappings();
    }
  }, [subscriptionId, fetchMappings]);

  const handleEdit = (mapping) => {
    setEditMapping(mapping);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditMapping(null);
    setModalVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await connectorAttributeMappings.remove(deleteTarget.attribute_mapping_id);
      message.success("Attribute mapping deleted");
      setDeleteTarget(null);
      fetchMappings();
    } catch (err) {
      message.error("Failed to delete attribute mapping");
    }
  };

  const handleToggleEnabled = async (mapping) => {
    try {
      await connectorAttributeMappings.update(mapping.attribute_mapping_id, { enabled: !mapping.enabled });
      message.success(mapping.enabled ? "Mapping disabled" : "Mapping enabled");
      fetchMappings();
    } catch (err) {
      message.error("Failed to update mapping");
    }
  };

  const columns = [
    {
      title: "Polly Attribute",
      dataIndex: "data_class_attribute_id",
      key: "attribute_name",
      render: (id) => attributeNames[id] || `Attribute #${id}`,
    },
    {
      title: "Direction",
      dataIndex: "direction",
      key: "direction",
      width: 110,
      render: (dir) =>
        dir === "OUTBOUND" ? (
          <span title="Outbound">{"\u2192"} Outbound</span>
        ) : (
          <span title="Inbound">{"\u2190"} Inbound</span>
        ),
    },
    {
      title: "Vendor Field",
      key: "vendorField",
      render: (_, record) => record.vendor_field || "-",
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) =>
        record.enabled ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a onClick={() => handleEdit(record)}>Edit</a>
          <Dropdown
            menu={{
              items: [
                {
                  key: "toggle",
                  label: record.enabled ? "Disable" : "Enable",
                  onClick: () => handleToggleEnabled(record),
                },
                {
                  key: "delete",
                  label: "Delete",
                  danger: true,
                  onClick: () => setDeleteTarget(record),
                },
              ],
            }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: 16 }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  if (loading) return null;

  if (mappings.length === 0 && !showEnabledOnly) {
    return (
      <div className="event-mappings-empty">
        {hiddenCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Attribute mapping list may be incomplete"
            description={`${hiddenCount} additional mapping(s) could not be loaded and are not shown.`}
            style={{ marginBottom: 16, textAlign: "left" }}
          />
        )}
        <h3>No attribute mappings configured.</h3>
        <p>
          Map Polly attributes to Klaviyo profile properties to sync data between systems.
        </p>
        <div className="empty-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="standard-button primary-orange-button"
            onClick={handleAdd}
          >
            Add Attribute Mapping
          </Button>
        </div>

        <AttributeMappingModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSaved={fetchMappings}
          editMapping={editMapping}
          attributeNames={attributeNames}
        />
      </div>
    );
  }

  return (
    <>
      {hiddenCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Attribute mapping list may be incomplete"
          description={`Showing ${fetchedCount} of ${fetchedCount + hiddenCount} attribute mappings. ${hiddenCount} additional mapping(s) could not be loaded and are not shown below.`}
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="event-mappings-toolbar">
        <div className="toolbar-left">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="standard-button primary-orange-button"
            onClick={handleAdd}
          >
            Add Attribute Mapping
          </Button>
          <Checkbox
            checked={showEnabledOnly}
            onChange={(e) => setShowEnabledOnly(e.target.checked)}
          >
            Show enabled only
          </Checkbox>
        </div>
      </div>
      <Table
        dataSource={mappings}
        columns={columns}
        rowKey="attribute_mapping_id"
        loading={loading}
        pagination={false}
        size="middle"
      />

      <AttributeMappingModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSaved={fetchMappings}
        editMapping={editMapping}
        attributeNames={attributeNames}
      />

      <Modal
        title="Delete Attribute Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the mapping for{" "}
          <strong>
            {(deleteTarget && attributeNames[deleteTarget.data_class_attribute_id]) ||
              `Attribute #${deleteTarget?.data_class_attribute_id}`}
          </strong>?
          This cannot be undone.
        </p>
      </Modal>
    </>
  );
}
